import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, AlertCircle, Loader2, ArrowLeft, Shield } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "/liquicent-logo.png";

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('Login Successful', {
        description: 'Welcome back, admin!',
      });
      navigate('/admin/leads');
    } else {
      toast.error('Login Failed', {
        description: result.error,
      });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center px-6 pt-20">
      <Helmet>
        <title>Admin Login | Liquicent</title>
      </Helmet>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <img 
              src={LOGO_URL} 
              alt="Liquicent" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-2xl font-semibold text-[#0F172A]">Liquicent</span>
          </Link>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Admin Login</h1>
          <p className="text-[#64748B]">Access the admin dashboard</p>
        </div>

        {/* Login Card */}
        <Card 
          className="p-8 bg-white border border-[#E5E7EB] rounded-2xl"
          style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Info Alert */}
            <div className="flex items-start gap-3 p-4 bg-[#1A3A8F]/5 border border-[#1A3A8F]/20 rounded-lg">
              <Shield className="w-5 h-5 text-[#1A3A8F] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-[#0F172A] font-medium mb-0.5">Admin Access Only</p>
                <p className="text-xs text-[#64748B]">
                  Only authorized administrators can access this area.
                </p>
              </div>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0F172A] text-sm font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#94A3B8] w-5 h-5" />
                <Input
                  id="email"
                  type="email"
                  required
                  placeholder="admin@liquicent.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#0F172A] text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#94A3B8] w-5 h-5" />
                <Input
                  id="password"
                  type="password"
                  required
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-[#1A3A8F] hover:bg-[#142C6D] text-white py-6 text-base font-medium rounded-lg transition-all"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center">
            <p className="text-xs text-[#64748B]">
              Need help? Contact{' '}
              <a href="mailto:support@liquicent.com" className="text-[#1A3A8F] hover:text-[#142C6D]">
                support@liquicent.com
              </a>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#64748B] hover:text-[#1A3A8F] text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
